import React, { useState, useMemo } from 'react'
import Swal from 'sweetalert2'
import { MdClose } from 'react-icons/md'

const isEmail = (value) => /^[^\s@<>,;]+@[^\s@<>,;]+\.[^\s@<>,;]+$/.test(value)
const MAX_RECIPIENTS = 50

// New email from the dashboard. Each recipient gets their own copy, sent in the site's email
// layout. `users` feeds the recipient suggestions; `quota` is the Resend plan usage.
const ComposeModal = ({ route, adminFetch, users = [], quota, initialTo = [], onClose, onSent }) => {
  const [recipients, setRecipients] = useState(initialTo)
  const [draft, setDraft] = useState('')
  const [subject, setSubject] = useState('')
  const [message, setMessage] = useState('')
  const [sending, setSending] = useState(false)
  const [error, setError] = useState('')

  const suggestions = useMemo(() => {
    const query = draft.trim().toLowerCase()
    if (query.length < 2) return []
    return users
      .filter(u => u.email && !recipients.includes(u.email.toLowerCase()))
      .filter(u => [u.email, u.firstname, u.lastname, `${u.firstname} ${u.lastname}`, u.username]
        .some(field => (field || '').toLowerCase().includes(query)))
      .slice(0, 6)
  }, [draft, users, recipients])

  const addRecipients = (text) => {
    const added = text.split(/[,;\s]+/).map(a => a.trim().toLowerCase()).filter(Boolean)
    const bad = added.filter(a => !isEmail(a))
    setRecipients(prev => [...new Set([...prev, ...added.filter(isEmail)])])
    setDraft(bad.join(', '))
    setError(bad.length ? `Not a valid email address: ${bad.join(', ')}` : '')
  }

  const onKeyDown = (e) => {
    if (['Enter', ',', ';', 'Tab'].includes(e.key) && draft.trim()) {
      e.preventDefault()
      addRecipients(draft)
    } else if (e.key === 'Backspace' && !draft && recipients.length) {
      setRecipients(prev => prev.slice(0, -1))
    }
  }

  const remaining = quota ? quota.remaining : null
  const total = recipients.length + (isEmail(draft.trim().toLowerCase()) ? 1 : 0)

  const send = async () => {
    const to = [...new Set([...recipients, ...(isEmail(draft.trim().toLowerCase()) ? [draft.trim().toLowerCase()] : [])])]
    if (!to.length) return setError('Add at least one recipient')
    if (!subject.trim()) return setError('Add a subject')
    if (!message.trim()) return setError('Write a message')

    setSending(true)
    setError('')
    try {
      const res = await (await adminFetch(`${route}/api/admin/emails/send`, {
        method: 'POST',
        body: JSON.stringify({ to, subject, message }),
      })).json()
      if (res.status !== 200) throw new Error(res.message || 'Could not send')
      Swal.fire({ toast: true, position: 'top-end', icon: 'success', showConfirmButton: false, timer: 3000,
        title: res.sent === 1 ? 'Email sent' : `${res.sent} emails sent` })
      onSent?.()
      onClose()
    } catch (e) {
      setError(e.message)
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="adm-modal-overlay" onClick={onClose}>
      <div className="adm-modal wide scroll" onClick={e => e.stopPropagation()}>
        <button className="adm-modal-close" onClick={onClose} aria-label="Close"><MdClose size={16} /></button>
        <p className="adm-modal-title">New email</p>
        <p className="adm-modal-sub">
          Sent in the Ollarin Mirror email layout. Each person gets their own copy.
          {remaining !== null && <> <strong>{remaining}</strong> left on your Resend plan today.</>}
        </p>

        <div className="adm-form-field">
          <label>To</label>
          <div className="adm-recipients">
            {recipients.map(address => (
              <span key={address} className="adm-recipient">
                {address}
                <button onClick={() => setRecipients(prev => prev.filter(a => a !== address))} aria-label={`Remove ${address}`}>
                  <MdClose size={12} />
                </button>
              </span>
            ))}
            <input
              value={draft}
              autoFocus
              placeholder={recipients.length ? '' : 'Name or email address'}
              onChange={e => setDraft(e.target.value)}
              onKeyDown={onKeyDown}
              onBlur={() => draft.trim() && !suggestions.length && addRecipients(draft)}
              onPaste={e => { e.preventDefault(); addRecipients(draft + e.clipboardData.getData('text')) }}
            />
          </div>
          {suggestions.length > 0 && (
            <div className="adm-suggestions">
              {suggestions.map(u => (
                <button key={u._id} onMouseDown={e => { e.preventDefault(); addRecipients(u.email) }}>
                  <span>{u.firstname} {u.lastname}</span>
                  <span className="adm-user-meta">{u.email}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="adm-form-field">
          <label>Subject</label>
          <input className="adm-form-input" value={subject} maxLength={200} onChange={e => setSubject(e.target.value)} />
        </div>

        <div className="adm-form-field">
          <label>Message</label>
          <textarea
            className="adm-form-input adm-form-textarea"
            rows={9}
            value={message}
            placeholder="Leave a blank line between paragraphs"
            onChange={e => setMessage(e.target.value)}
          />
        </div>

        {total > MAX_RECIPIENTS && <p className="adm-form-error">Send to at most {MAX_RECIPIENTS} people at once.</p>}
        {remaining !== null && total > remaining && <p className="adm-form-error">That's more than the {remaining} emails your Resend plan allows right now.</p>}
        {error && <p className="adm-form-error">{error}</p>}

        <div className="adm-modal-actions">
          <button className="adm-btn ghost" onClick={onClose}>Cancel</button>
          <button className="adm-btn primary" disabled={sending || total > MAX_RECIPIENTS} onClick={send}>
            {sending ? 'Sending…' : total > 1 ? `Send to ${total} people` : 'Send'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default ComposeModal
